import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { LogoLockup, LogoMark } from "@/components/Logo";
import { CalendarDays, Clock, Check, HelpCircle, X, Youtube, ExternalLink, ChevronDown, Music } from "lucide-react";

function fmt(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }
  catch (e) { return d; }
}
const statusIcon = (s) => s === "confirmed" ? <Check className="w-4 h-4 text-emerald-400" /> : s === "declined" ? <X className="w-4 h-4 text-rose-400" /> : <HelpCircle className="w-4 h-4 text-amber-400" />;

export default function PublicShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(undefined);
  const [openSong, setOpenSong] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/service/${shareId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [shareId]);

  if (data === undefined) return <div className="wf-app-bg min-h-screen flex items-center justify-center"><LogoMark size={48} /></div>;
  if (data === null) return (
    <div className="wf-app-bg min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <LogoMark size={56} />
      <h1 className="font-display text-2xl font-bold text-[#F7F5F0] mt-4">Schedule unavailable</h1>
      <p className="text-[#8A97AC] mt-2">This link may have been turned off. Please ask your worship leader for an updated link.</p>
    </div>
  );

  const { service, team, flow, resources, church } = data;

  return (
    <div className="wf-app-bg min-h-screen pb-16">
      <header className="wf-glass border-b border-[#E5C378]/12 px-4 py-4 flex items-center justify-between sticky top-0 z-20">
        <LogoLockup subtitle={church?.toUpperCase() || "WORSHIP FLOW"} />
        <span className="eyebrow hidden sm:block">Shared schedule</span>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-br from-[#121B2F] to-[#0A0F1D] border border-[#E5C378]/25 rounded-2xl p-6 mb-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#E5C378]/10 rounded-full blur-3xl" />
          <div className="eyebrow mb-1">{service.type}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">{service.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-[#A0AEC0] mt-3">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmt(service.date)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {service.time}</span>
            {service.rehearsal?.date && <span className="flex items-center gap-1.5">Rehearsal: {fmt(service.rehearsal.date)} {service.rehearsal.time}</span>}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="wf-card rounded-2xl p-5">
            <h2 className="font-display font-semibold text-[#F7F5F0] mb-3">Team</h2>
            <div className="space-y-2">
              {team.length === 0 && <p className="text-sm text-[#6E7D97]">Team not finalised yet.</p>}
              {team.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-[#0B101D] border border-white/5 rounded-lg p-2.5">
                  <div><div className="text-sm text-[#F7F5F0]">{a.name}</div><div className="text-xs text-[#E5C378]">{a.slot}</div></div>
                  {statusIcon(a.status)}
                </div>
              ))}
            </div>
          </div>

          <div className="wf-card rounded-2xl p-5">
            <h2 className="font-display font-semibold text-[#F7F5F0] mb-3">Service Flow</h2>
            <ol className="space-y-1.5">
              {flow.map((f, i) => (
                <li key={i} className="text-sm text-[#B8C1D1]">
                  <span className="text-[#E5C378] font-semibold">{f.type}</span> — {f.title}
                  {f.note && <span className="block text-xs text-[#6E7D97] italic ml-1">{f.note}</span>}
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="wf-card rounded-2xl p-5 mt-6">
          <h2 className="font-display font-semibold text-[#F7F5F0] mb-3 flex items-center gap-2"><Music className="w-4 h-4 text-[#E5C378]" /> Songs & Resources</h2>
          <div className="space-y-2">
            {resources.map((r, i) => (
              <div key={i} className="bg-[#0B101D] border border-white/5 rounded-xl overflow-hidden">
                <button onClick={() => setOpenSong(openSong === i ? null : i)} className="w-full flex items-center justify-between p-3 text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-mono text-[#6E7D97] w-5">{i + 1}</span>
                    <div className="min-w-0"><div className="text-sm text-[#F7F5F0] truncate">{r.title}</div>
                      <div className="flex gap-2 mt-1">
                        <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-[#E5C378]/15 text-[#E5C378] border border-[#E5C378]/30">{r.key}</span>
                        <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-sky-500/15 text-sky-300 border border-sky-500/30">{r.bpm} BPM</span>
                      </div>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#6E7D97] transition-transform ${openSong === i ? "rotate-180" : ""}`} />
                </button>
                {openSong === i && (
                  <div className="px-3 pb-3 border-t border-white/5 pt-3">
                    <div className="flex gap-3 mb-3">
                      {r.links?.youtube && <a href={r.links.youtube} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378]"><Youtube className="w-4 h-4" /> YouTube</a>}
                      {r.links?.spotify && <a href={r.links.spotify} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378]"><ExternalLink className="w-4 h-4" /> Spotify</a>}
                    </div>
                    {r.sections?.length ? (
                      <div className="font-mono text-sm">
                        {r.sections.map((sec, si) => (
                          <div key={si} className="mb-3">
                            <div className="text-[#7EA293] uppercase tracking-wider text-xs font-bold mb-1">{sec.name}</div>
                            {sec.lines.map((ln, li) => (
                              <div key={li}>
                                {ln.chords?.trim() && <pre className="text-[#FFD56B] font-bold m-0 whitespace-pre-wrap">{ln.chords}</pre>}
                                {ln.lyrics?.trim() && <div className="text-[#E7EAF0]">{ln.lyrics}</div>}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : r.lyrics ? <div className="whitespace-pre-wrap text-sm text-[#E7EAF0]">{r.lyrics}</div> : <p className="text-xs text-[#6E7D97]">No chart or lyrics shared.</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-[10px] text-[#6E7D97] italic mt-8">"At the right time, I, the LORD, will make it happen." — Isaiah 60:22 · Powered by Worship Flow</p>
      </div>
    </div>
  );
}
